import { Icon } from "@deligoo/ui"
import L, { LatLngTuple, MarkerCluster } from "leaflet"
import { MutableRefObject, useContext, useEffect, useMemo, useRef, useState } from "react"
import { renderToString } from "react-dom/server"
import { FeatureGroup, Marker, Polygon } from "react-leaflet"
import MarkerClusterGroup, { MarkerClusterGroupProps } from "react-leaflet-markercluster"
import { shallowEqual } from "react-redux"

import { MapContext, useAppSelector } from "@/App"
import colorsCls from "@/scss/colors.module.scss"
import { selectClientDeliveryZone, selectFilteredClientsIds } from "@/store/clients"
import { selectActiveDriversIds, selectInactiveDriversIds } from "@/store/drivers"
import {
  selectActiveOrdersIdsByDriverId,
  selectFilteredDriversIdsForFilteredOrders,
  selectFilteredOrdersIds,
  selectFilteredOrdersIdsByDriverId,
  selectFilteredOrdersWithDriversIds,
} from "@/store/orders"
import { selectCurrentTeamId, selectHasCurrentTeamDeliveryZone } from "@/store/teams"
import { Driver, Order } from "@/types"
import { DriverUtils, escapeRegex, fetchTeamDeliveryZone, OrderUtils, parseLineString, zIndexMapMarkers } from "@/utils"

import { getSelectedClientLayers, MapLayer } from "../../MapLayers"
import { ClientsMarkers, getClientMarkerIcon } from "../Markers/ClientsMarkers"
import { DriverMarker, DriversMarkers } from "../Markers/DriverMarkers"
import { OrdersMarkers } from "../Markers/OrderMarkers"
import { DriverTasksRoute } from "./DriverTasksRoute"
import { DriverTasksRouteHistory } from "./DriverTasksRouteHistory"

export const DefaultLayer = ({
  layers,
  clusterRef,
}: {
  layers: MapLayer[]
  clusterRef: MutableRefObject<MarkerClusterGroup<MarkerClusterGroupProps> | null>
}) => {
  const selectedClientLayers = getSelectedClientLayers(layers)

  const currentClientLayersSelector = useMemo(
    () => selectFilteredClientsIds((client) => selectedClientLayers.includes(client.location_type)),
    [selectedClientLayers]
  )

  return (
    <FeatureGroup>
      <MarkerClusterGroup
        ref={clusterRef}
        maxClusterRadius={40}
        disableClusteringAtZoom={10}
        spiderfyOnMaxZoom={false}
        removeOutsideVisibleBounds
      >
        <OrdersMarkers selector={selectActiveOrdersIds} zIndexOffset={zIndexMapMarkers.active_orders} />

        {layers.includes("finished_orders") && (
          <OrdersMarkers selector={selectFinishedOrdersIds} zIndexOffset={zIndexMapMarkers.finished_orders} />
        )}
        {layers.includes("failed_orders") && (
          <OrdersMarkers selector={selectFailedOrdersIds} zIndexOffset={zIndexMapMarkers.failed_orders} />
        )}
        {layers.includes("canceled_orders") && (
          <OrdersMarkers selector={selectCanceledOrdersIds} zIndexOffset={zIndexMapMarkers.canceled_orders} />
        )}

        {layers.includes("offline_drivers") && (
          <DriversMarkers selector={selectInactiveDriversIds} zIndexOffset={zIndexMapMarkers.offline_drivers} />
        )}
        {layers.includes("online_drivers") && (
          <DriversMarkers selector={selectActiveDriversIds} zIndexOffset={zIndexMapMarkers.online_drivers} />
        )}
      </MarkerClusterGroup>
      <MarkerClusterGroup
        maxClusterRadius={10}
        removeOutsideVisibleBounds
        iconCreateFunction={() => getClientMarkerIcon("cluster")}
      >
        {Boolean(selectedClientLayers.length) && <ClientsMarkers selector={currentClientLayersSelector} />}
      </MarkerClusterGroup>
    </FeatureGroup>
  )
}

export const DeliveryZonesLayer = ({ layers }: { layers: MapLayer[] }) => {
  const currentTeamId = useAppSelector(selectCurrentTeamId)
  const hasTeamDeliveryZone = useAppSelector(selectHasCurrentTeamDeliveryZone)
  const [teamDeliveryZone, setTeamDeliveryZone] = useState<{ polygon: string } | null>()
  const teamDeliveryZoneLayerRef = useRef<Polygon | null>(null)
  const clientDeliveryZone = useAppSelector(selectClientDeliveryZone, shallowEqual)

  const teamDeliveryZoneCoords = useMemo(() => {
    if (!teamDeliveryZone) return

    return parseLineString(teamDeliveryZone.polygon)
  }, [teamDeliveryZone])

  const clientDeliveryZoneCoords = useMemo(() => {
    if (!clientDeliveryZone) return

    return parseLineString(clientDeliveryZone.data.polygon)
  }, [clientDeliveryZone])

  const shouldShowTeamDeliveryZone = layers.includes("team_delivery_zone")

  useEffect(() => {
    setTeamDeliveryZone(null)

    async function getTeamDeliveryZone() {
      if (!shouldShowTeamDeliveryZone || !hasTeamDeliveryZone || !currentTeamId) return

      const { json } = await fetchTeamDeliveryZone(currentTeamId)
      if (json?.data) setTeamDeliveryZone(json.data)
    }

    getTeamDeliveryZone()
  }, [shouldShowTeamDeliveryZone, hasTeamDeliveryZone, currentTeamId])

  useEffect(() => {
    if (!shouldShowTeamDeliveryZone || !teamDeliveryZoneLayerRef.current) return
    // This is to ensure it doesn't overlay client delivery zone
    // AFAIK z-index strategy is not applicable here with svg elements
    teamDeliveryZoneLayerRef.current.leafletElement.bringToBack()
  }, [shouldShowTeamDeliveryZone])

  return (
    <>
      {shouldShowTeamDeliveryZone && teamDeliveryZoneCoords && (
        <Polygon ref={teamDeliveryZoneLayerRef} color={colorsCls.primary} positions={teamDeliveryZoneCoords} />
      )}
      {clientDeliveryZoneCoords && <Polygon color={colorsCls.secondary} positions={clientDeliveryZoneCoords} />}
    </>
  )
}

export const DriverTasksRouteHistoryLayer = ({ driverId }: { driverId: Driver["id"] }) => {
  const selectDriverCompletedOrdersIds = useMemo(
    () =>
      selectFilteredOrdersIdsByDriverId(driverId, (order: Order) =>
        ["finished", "canceled", "failed"].includes(order.state)
      ),
    [driverId]
  )

  const featureGroupRef = useRef<FeatureGroup | null>(null)

  useFeatureGroupBounding(featureGroupRef)

  return (
    <FeatureGroup ref={featureGroupRef}>
      <DriverTasksRouteHistory driverId={driverId} />
      <OrdersMarkers selector={selectDriverCompletedOrdersIds} zIndexOffset={zIndexMapMarkers.finished_orders} />
      <DriverMarker driverId={driverId} zIndexOffset={zIndexMapMarkers.online_drivers} />
    </FeatureGroup>
  )
}

export const DriverTasksLayer = ({ driverId }: { driverId: Driver["id"] }) => {
  const selectDriverActiveOrdersIds = useMemo(() => selectActiveOrdersIdsByDriverId(driverId), [driverId])

  const featureGroupRef = useRef<FeatureGroup | null>(null)

  useFeatureGroupBounding(featureGroupRef)

  return (
    <FeatureGroup ref={featureGroupRef}>
      <MarkerClusterGroup
        maxClusterRadius={20}
        showCoverageOnHover={false}
        iconCreateFunction={createDriverTasksClusterIcon}
        // clusterPane="driver-tasks-route-pane"
      >
        <DriverTasksRoute driverId={driverId} />
      </MarkerClusterGroup>

      <OrdersMarkers selector={selectDriverActiveOrdersIds} zIndexOffset={zIndexMapMarkers.finished_orders} />
      <DriverMarker driverId={driverId} zIndexOffset={zIndexMapMarkers.online_drivers} />
    </FeatureGroup>
  )
}

function createDriverTasksClusterIcon(cluster: MarkerCluster) {
  return L.divIcon({
    html: `<div>${cluster.getChildCount()}</div>`,
    className: "driver-tasks-cluster-marker",
    iconSize: L.point(32, 32, true),
  })
}

export const OrdersSearchLayer = ({ layers }: { layers: MapLayer[] }) => {
  const ordersPanelSearchValue = useAppSelector((state) => state.ui.ordersPanelSearchValue)

  const ordersSelector = useMemo(
    () =>
      selectFilteredOrdersWithDriversIds(
        (order) => OrderUtils.filterBySearch(order, ordersPanelSearchValue),
        (order) => OrderUtils.filterByLayers(order, layers)
      ),
    [ordersPanelSearchValue, layers]
  )

  const driversSelector = useMemo(
    () =>
      selectFilteredDriversIdsForFilteredOrders(
        [(order) => OrderUtils.filterBySearch(order, ordersPanelSearchValue)],
        [(driver) => DriverUtils.filterByLayers(driver, layers)]
      ),
    [ordersPanelSearchValue, layers]
  )

  const featureGroupRef = useRef<FeatureGroup | null>(null)

  return (
    <FeatureGroup ref={featureGroupRef}>
      <DriversMarkers selector={driversSelector} zIndexOffset={zIndexMapMarkers.online_drivers} />
      <OrdersMarkers selector={ordersSelector} zIndexOffset={zIndexMapMarkers.active_orders} />
    </FeatureGroup>
  )
}

export const ClientsSearchLayer = ({ layers }: { layers: MapLayer[] }) => {
  const searchValue = useAppSelector((state) => state.ui.clientsSearchValue)

  const selectedClientLayers = getSelectedClientLayers(layers)

  const clientsSearchValueSelector = useMemo(() => {
    const searchString = escapeRegex(searchValue).trim()
    const searchRegexp = new RegExp(searchString, "i")

    return selectFilteredClientsIds(
      (client) => selectedClientLayers.includes(client.location_type) && searchRegexp.test(client.name)
    )
  }, [selectedClientLayers, searchValue])

  const featureGroupRef = useRef<FeatureGroup | null>(null)

  useFeatureGroupBounding(featureGroupRef)

  return (
    <FeatureGroup ref={featureGroupRef}>
      <MarkerClusterGroup
        maxClusterRadius={10}
        removeOutsideVisibleBounds
        iconCreateFunction={() => getClientMarkerIcon("cluster")}
      >
        <ClientsMarkers selector={clientsSearchValueSelector} />
      </MarkerClusterGroup>
    </FeatureGroup>
  )
}

const DEFAULT_PIN_ICON = L.divIcon({
  className: "pin-marker",
  html: renderToString(<Icon name="location" className="marker-icon" />),
  iconAnchor: [10, 10],
  popupAnchor: [0, -16],
  iconSize: [32, 32],
})

export const PinnedLocationLayer = () => {
  const pinnedLocation = useAppSelector((state) => state.ui.pinnedLocation, shallowEqual)

  const featureGroupRef = useRef<FeatureGroup | null>(null)

  useFeatureGroupBounding(featureGroupRef, [pinnedLocation])

  if (!pinnedLocation) return null

  const position: LatLngTuple = [pinnedLocation.position.lat, pinnedLocation.position.lng]

  return (
    <FeatureGroup ref={featureGroupRef}>
      <Marker icon={DEFAULT_PIN_ICON} position={position} />
    </FeatureGroup>
  )
}

function useFeatureGroupBounding(featureGroupRef: MutableRefObject<FeatureGroup | null>, deps: unknown[] = []) {
  const mapRef = useContext(MapContext)

  useEffect(() => {
    if (!mapRef?.current || !featureGroupRef?.current) return

    const groupBounds = featureGroupRef.current.leafletElement.getBounds()

    if (Object.keys(groupBounds).length)
      mapRef.current.leafletElement.flyToBounds(groupBounds, {
        animate: false,
        padding: [16, 16],
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureGroupRef, mapRef, ...deps])
}

const selectActiveOrdersIds = selectFilteredOrdersIds((order: Order) => !OrderUtils.isFinished(order))

const selectFinishedOrdersIds = selectFilteredOrdersIds((order: Order) => order.state === "finished")

const selectFailedOrdersIds = selectFilteredOrdersIds((order: Order) => order.state === "failed")

const selectCanceledOrdersIds = selectFilteredOrdersIds((order: Order) => order.state === "canceled")
